// /onboarding/shape (ShapeStep → staff) — shape 1, «من يعمل معك؟» (board 25 v2 shape--1):
// the team invites (ADR-0016), the one sixth step whose entities exist on the
// wire today. Every other shape goes on to done for now (shapeOf ≠ staff).
// The invite row posts profile POST /v1/business/{id}/invites per number;
// the list shows the owner first, then every invite — the ones profile
// already holds merged with the ones sent here. «إنهاء الإعداد» and
// «تخطّي الآن» both go to done. States: default · adding · invalid
// (· error-network / error-server through the shell).

import { useMemo, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, ChevronDown, UserPlus } from 'lucide-react';
import { PrimaryAction } from '@/components/Button';
import { isApiError } from '@/lib/api';
import { sessionStore } from '@/lib/auth';
import { formatIraqiPhone, normalizeIraqiPhone } from '@/lib/digits';
import { useLayout } from '@/lib/layout';
import { groupPhone, Msg, phoneDigits, ROLE_LABEL } from '../../components/widgets';
import { Wizard, type WizardError } from '../../components/Wizard';
import { customersAcc, PHONE, SHAPE1 } from '../../copy';
import { failureOf, createInvite, listInvites, type TeamInvite } from '../../data/api';
import { finishShape, unscheduledRoute } from '../../data/enroll';
import { ONB } from '../../data/flow';
import { shapeOf } from '../../data/shapes';
import { teamNote } from '../../preview';
import { onboardingStore, useOnboarding } from '../../state';
import { accountOwnerName, accountPhone, useBusinessPreview } from '../Business';

function phoneKey(phone: string): string {
  return normalizeIraqiPhone(phone) ?? phone.trim();
}

/** profile's rows first (they carry the status), then the ones sent here that profile has not listed — one row per number. */
function mergeInvites(listed: TeamInvite[], sent: TeamInvite[]): TeamInvite[] {
  const out: TeamInvite[] = [];
  const seen = new Set<string>();
  for (const inv of [...listed, ...sent]) {
    const k = phoneKey(inv.phone);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(inv);
  }
  return out;
}

const PHONE_INVALID: ReactNode = (
  <>
    {PHONE.invalid}
    <bdi dir="ltr" className="ltr">
      {PHONE.mask}
    </bdi>
  </>
);

function Ltr({ children }: { children: string }) {
  return (
    <bdi dir="ltr" className="ltr">
      {children}
    </bdi>
  );
}

export function TeamStep() {
  const navigate = useNavigate();
  const mobile = useLayout() === 'phone';
  const { createdId, type, invites } = useOnboarding();
  const authed = sessionStore.isAuthenticated();
  // A category with no known shape books as staff (the step map and the PUT agree).
  const staff = (shapeOf(type) ?? 'staff') === 'staff';
  const businessId = createdId ?? '';
  const listed = useQuery({
    queryKey: ['onboarding', 'invites', businessId],
    queryFn: () => listInvites(businessId),
    enabled: authed && !!businessId && staff,
    staleTime: Infinity,
    retry: 0,
  });
  const all = useMemo(() => mergeInvites(listed.data ?? [], invites), [listed.data, invites]);
  const { profile, preview } = useBusinessPreview('shape', all);
  const [digits, setDigits] = useState('');
  const [error, setError] = useState<ReactNode>(null);
  const [adding, setAdding] = useState(false);
  const [failure, setFailure] = useState<Pick<WizardError, 'kind' | 'ref' | 'detail'> | null>(null);

  if (!authed) return <Navigate to={ONB.phone} replace />;
  if (!createdId) return <Navigate to={ONB.hours} replace />;
  const pending = unscheduledRoute();
  if (pending) return <Navigate to={pending} replace />;
  if (!staff) return <Navigate to={ONB.done} replace />;

  const add = async () => {
    if (adding) return;
    const phone = normalizeIraqiPhone(`0${digits}`);
    if (!phone) {
      setError(PHONE_INVALID);
      return;
    }
    setAdding(true);
    setError(null);
    setFailure(null);
    try {
      const inv = await createInvite(createdId, phone);
      onboardingStore.set({ invites: mergeInvites(onboardingStore.get().invites, [inv]) });
      setDigits('');
    } catch (e) {
      // The server's own refusal of this number (422 invalid, 409 already invited…) stays under the row.
      if (isApiError(e) && e.status > 0 && e.status < 500) setError(e.detail ?? e.message);
      else setFailure({ ...failureOf(e) });
    } finally {
      setAdding(false);
    }
  };

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    e.stopPropagation();
    void add();
  };

  const ownerName = accountOwnerName();
  const ownerPhone = formatIraqiPhone(accountPhone());
  const state = adding ? 'adding' : error ? 'invalid' : 'default';

  return (
    <Wizard
      screen="shape"
      testId="onb-shape-step"
      state={state}
      list
      question={SHAPE1.title}
      sub={SHAPE1.sub}
      primary={
        <PrimaryAction type="submit" size="lg" icon={ArrowLeft} block={mobile}>
          {SHAPE1.finish}
        </PrimaryAction>
      }
      tertiary={
        <button type="button" className="onb-tert" data-testid="onb-skip-shape" onClick={() => finishShape(navigate)}>
          {SHAPE1.skipNow}
        </button>
      }
      onSubmit={() => finishShape(navigate)}
      error={failure ? { kind: failure.kind, ref: failure.ref, detail: failure.detail, onRetry: () => void add() } : null}
      panel={{ phase: 'preview', profile, preview }}
    >
      <div className="onb-inviterow" dir="ltr">
        <span className="onb-phone-cc ltr" aria-hidden="true">
          +964
          <ChevronDown />
        </span>
        <input
          className="onb-inviterow-input ltr"
          type="tel"
          inputMode="tel"
          autoComplete="off"
          aria-label={SHAPE1.title}
          aria-invalid={error ? true : undefined}
          placeholder={SHAPE1.placeholder}
          data-testid="onb-invite-input"
          value={groupPhone(digits)}
          disabled={adding}
          onChange={(e) => {
            setError(null);
            setDigits(phoneDigits(e.target.value));
          }}
          onKeyDown={onKey}
        />
        <button type="button" className="onb-inviterow-add" aria-label={SHAPE1.add} data-testid="onb-invite-add" disabled={adding} onClick={() => void add()}>
          <UserPlus aria-hidden="true" />
        </button>
      </div>
      {error && <Msg testId="onb-invite-error">{error}</Msg>}
      <div className="onb-listcard onb-team" data-testid="onb-team">
        <div className="onb-row">
          <span className="onb-avatar" aria-hidden="true">
            {ownerName ? ownerName.charAt(0) : '·'}
          </span>
          <div className="onb-row-text">
            <div className="onb-row-title">{ownerName ?? <Ltr>{ownerPhone}</Ltr>}</div>
            <div className="onb-row-sub">{SHAPE1.you}</div>
          </div>
        </div>
        {all.map((inv) => {
          const name = inv.name?.trim() ?? '';
          const phone = formatIraqiPhone(inv.phone);
          const role = ROLE_LABEL[inv.role] ?? inv.role;
          const joined = inv.status === 'claimed' || inv.status === 'accepted';
          return (
            <div className="onb-row" key={inv.id ?? phoneKey(inv.phone)}>
              <span className="onb-avatar" aria-hidden="true">
                {name ? name.charAt(0) : '·'}
              </span>
              <div className="onb-row-text">
                <div className="onb-row-title">{name || <Ltr>{phone}</Ltr>}</div>
                <div className="onb-row-sub">
                  {name ? (
                    <>
                      <Ltr>{phone}</Ltr> · {role}
                    </>
                  ) : (
                    role
                  )}
                </div>
              </div>
              <span className="onb-pill" data-tone={joined ? 'ok' : 'warn'}>
                {joined ? SHAPE1.joined : SHAPE1.sent}
              </span>
            </div>
          );
        })}
      </div>
      <Msg tone="info" testId="onb-team-note">
        {teamNote(type?.id, customersAcc(profile))}
      </Msg>
    </Wizard>
  );
}
